import { format as d3Format } from 'd3-format';
import React from 'react';

import { TRADE_DIRECTION_UNSPECIFIED } from 'src/util/constants';
import { i18nDecimalFormat } from 'src/util/i18n/handler';

import {
  BUY, EXPORTS,
  IMPORTS, SELL, UNIT_CARBON,
  UNIT_CARBON_TOOLTIP_FORMAT,
  UNIT_CURRENCY, UNIT_CURRENCY_TOOLTIP_FORMAT_LARGE_VALUE,
  UNIT_CURRENCY_TOOLTIP_FORMAT_LARGE_VALUE_THRESHOLD,
  UNIT_CURRENCY_TOOLTIP_FORMAT_SMALL_VALUE, UNIT_ENERGY,
  UNIT_ENERGY_FORMAT,
} from '../components/chartConstants';

// if the x position is half way through the chart, flip the tooltip to the left
export const getTooltipPosition = (x, chartWidth) => {
  if (chartWidth / x < 2) {
    return x - 230;
  }
  return x;
};

/**
 * Returns the direction label for the tooltip
 * @param {EXPORTS | IMPORTS} dir
 * @param {import('react-intl').IntlShape} intl - i18n react-intl
 * @returns {string} - i18n label
 */
export const getDirectionLabel = (dir, intl) => {
  if (!intl || !dir) {
    return TRADE_DIRECTION_UNSPECIFIED;
  }
  switch (dir) {
    case EXPORTS:
      return intl.formatMessage({ id: 'common.entities.exports.label', defaultMessage: 'Exports' });
    case IMPORTS:
      return intl.formatMessage({ id: 'common.entities.imports.label', defaultMessage: 'Imports' });
    default:
      return TRADE_DIRECTION_UNSPECIFIED;
  }
};
/**
 * Forms the meter label for the tooltip
 * @param {string} header - import or export i18n label
 * @param {object} label -  title and identifier
 * @returns {React.ReactElement} - meter chart tooltip.
 */
export const getMeterChartToolipLabel = (header, label = {}) => {
  const { title, identifier } = label;
  let subHeader = title || '';
  subHeader = identifier ? `${title} (${identifier})` : title;
  return (
    <>
      {header}
      <>
        {subHeader ? ` - ${subHeader}` : ''}
        {' '}
      </>
    </>
  );
};

/**
 * Calculates the maximum absolute value from an array of numbers.
 * @param {Array<number>} values - Array of numeric values
 * @returns {number} - The maximum absolute value from the array, or 1 if the array is empty
 */
export const getMaxAbsValue = (values) => {
  if (!values || values.length === 0) {
    return null;
  }
  return (Math.max(...(values.map((value) => Math.abs(value)))));
};

/**
 * Extracts all trade values from a data object containing buy and sell directions.
 * @param {object} data - The data object containing trade information
 * @returns {Array<number>} - Array containing all extracted trade values
 */
export const extractTradeValues = (data) => {
  const allItems = [];
  if (!data) return allItems;

  [BUY, SELL].forEach((direction) => {
    data[direction]?.forEach((item) => {
      allItems.push(item.value);
    });
  });

  return allItems;
};

/**
 * Determines the appropriate tooltip format function based on the unit of measure
 * and set of values to be present in the tooltip.
 * @param {UNIT_CURRENCY | UNIT_CARBON | UNIT_ENERGY} unit - The unit of the value.
 * @param {Array<number>} [values=defaultFunc] - The full set of values to be formatted
 * in the tooltip.
 * @returns {Function} - The appropriate tooltip format function.
 */
export const getTooltipFormatFunc = (unit, values) => {
  const defaultFunc = (value, unitLabel) => `${(value)}${unitLabel}`;
  if (!unit) return defaultFunc;

  switch (unit) {
    case UNIT_CURRENCY: {
      const maxAbsValue = getMaxAbsValue(values);

      if (maxAbsValue === null
        || maxAbsValue >= UNIT_CURRENCY_TOOLTIP_FORMAT_LARGE_VALUE_THRESHOLD) {
        return (value, unitLabel) => `${i18nDecimalFormat(d3Format(UNIT_CURRENCY_TOOLTIP_FORMAT_LARGE_VALUE)(value))}${unitLabel}`;
      }
      return (value, unitLabel) => `${i18nDecimalFormat(d3Format(UNIT_CURRENCY_TOOLTIP_FORMAT_SMALL_VALUE)(value))}${unitLabel}`;
    }

    case UNIT_CARBON:
      return (value, unitLabel) => `${i18nDecimalFormat(d3Format(UNIT_CARBON_TOOLTIP_FORMAT)(value))}${unitLabel}`;

    case UNIT_ENERGY:
      return (value, unitLabel) => `${i18nDecimalFormat(d3Format(UNIT_ENERGY_FORMAT)(value))}${unitLabel}`;

    default:
      return () => null;
  }
};
